// The "underlying tiling" of an export: leaf tiles at that iteration, as a
// TileSet, with the edge segments the renderers draw and a lookup for the tile
// under the drawing tip. Sources: TIL1 files (Hat, Spectre, Pinwheel, Penrose),
// the Hilbert grid (computed) and the Quaquaversal prisms (from the rules).
import type { CurveGeometry, ExportMeta } from './types.ts';
import { decodeTiles, tilePoint, type TilePolygons } from './tiles-format.ts';
import { buildQuaquaPrisms } from './generators/quaquaversal.ts';
import { QUAQUA_RULES } from './generators/quaqua-rules.ts';

export interface SpatialIndex { x: number; y: number; cell: number; cols: number; rows: number; offsets: Uint32Array; ids: Uint32Array }

export type TileSet =
  | (TilePolygons & { edges: [number, number][]; index: SpatialIndex })
  | { kind: 'grid'; dims: 2; n: number; side: number; origin: [number, number] }   // unit squares from origin
  | { kind: 'prisms'; dims: 3; n: number; affines: Float64Array; base: number[][]; edges: [number, number][] };

export const PRISM_EDGES: [number, number][] = [[0, 1], [1, 2], [2, 0], [3, 4], [4, 5], [5, 3], [0, 3], [1, 4], [2, 5]];

const cache = new Map<string, TileSet>();
const inflight = new Map<string, Promise<TileSet>>();
const MAX_CACHED = 4;

/** Human name of the tiles, for the status line. */
export function tileNoun(curveId: string): string {
  if (curveId.startsWith('hilbert')) return 'squares';
  if (curveId.startsWith('pinwheel')) return 'triangles';
  if (curveId.startsWith('penrose')) return 'Robinson triangles';
  if (curveId.startsWith('hat')) return 'Hat tiles';
  if (curveId.startsWith('spectre')) return 'Spectre tiles';
  if (curveId.startsWith('quaqua')) return 'triangular prisms';
  return 'tiles';
}

export function loadTiles(exp: ExportMeta, geom: CurveGeometry): Promise<TileSet> | null {
  const src = exp.tiles;
  if (!src) return null;
  const hit = cache.get(exp.id);
  if (hit) return Promise.resolve(hit);
  let p = inflight.get(exp.id);
  if (p) return p;
  p = (async () => {
    let t: TileSet;
    if (src.kind === 'grid') {
      // Hilbert: points sit at cell centres (i + 0.5 - half), so cell (i, j) starts at (i - half, j - half).
      const side = Math.round(Math.sqrt(geom.nPoints));
      t = { kind: 'grid', dims: 2, n: side * side, side, origin: [geom.bbox.min[0] - 0.5, geom.bbox.min[1] - 0.5] };
    } else if (src.kind === 'prisms') {
      const affines = buildQuaquaPrisms(exp.depth);
      t = { kind: 'prisms', dims: 3, n: affines.length / 12, affines, base: QUAQUA_RULES.vertices, edges: PRISM_EDGES };
    } else {
      const r = await fetch(src.url);
      if (!r.ok) throw new Error(`tiles: HTTP ${r.status}`);
      const poly = decodeTiles(await r.arrayBuffer());
      const edges: [number, number][] = poly.base.map((_, i) => [i, (i + 1) % poly.base.length]);
      t = { ...poly, edges, index: buildIndex(poly) };
    }
    cache.set(exp.id, t);
    while (cache.size > MAX_CACHED) { const k = cache.keys().next().value as string; cache.delete(k); }
    inflight.delete(exp.id);
    return t;
  })();
  inflight.set(exp.id, p);
  p.catch(() => inflight.delete(exp.id));
  return p;
}

/** All tile edges as disjoint segments (pairs), for the overlay. */
export function tileEdgeSegments(t: TileSet): Float32Array {
  if (t.kind === 'grid') {
    // Grid lines instead of 4 edges per square: 2 (side + 1) segments.
    const s = t.side, out = new Float32Array((s + 1) * 2 * 4);
    let k = 0;
    for (let i = 0; i <= s; i++) {
      out[k++] = t.origin[0] + i; out[k++] = t.origin[1]; out[k++] = t.origin[0] + i; out[k++] = t.origin[1] + s;
      out[k++] = t.origin[0]; out[k++] = t.origin[1] + i; out[k++] = t.origin[0] + s; out[k++] = t.origin[1] + i;
    }
    return out;
  }
  if (t.kind === 'prisms') {
    const out = new Float32Array(t.n * t.edges.length * 6);
    let k = 0;
    for (let i = 0; i < t.n; i++) for (const [a, b] of t.edges) {
      const pa = prismPoint(t.affines, i, t.base[a]), pb = prismPoint(t.affines, i, t.base[b]);
      out[k++] = pa[0]; out[k++] = pa[1]; out[k++] = pa[2]; out[k++] = pb[0]; out[k++] = pb[1]; out[k++] = pb[2];
    }
    return out;
  }
  const out = new Float32Array(t.n * t.edges.length * 4);
  let k = 0;
  for (let i = 0; i < t.n; i++) for (const [a, b] of t.edges) {
    const pa = tilePoint(t, i, t.base[a]), pb = tilePoint(t, i, t.base[b]);
    out[k++] = pa[0]; out[k++] = pa[1]; out[k++] = pb[0]; out[k++] = pb[1];
  }
  return out;
}

/** Outline of one tile as segments, or null when `id` is out of range. */
export function tileOutline(t: TileSet, id: number): Float32Array | null {
  if (id < 0 || id >= t.n) return null;
  if (t.kind === 'grid') {
    const x = t.origin[0] + (id % t.side), y = t.origin[1] + Math.floor(id / t.side);
    return new Float32Array([x, y, x + 1, y, x + 1, y, x + 1, y + 1, x + 1, y + 1, x, y + 1, x, y + 1, x, y]);
  }
  if (t.kind === 'prisms') {
    const out = new Float32Array(t.edges.length * 6);
    let k = 0;
    for (const [a, b] of t.edges) {
      const pa = prismPoint(t.affines, id, t.base[a]), pb = prismPoint(t.affines, id, t.base[b]);
      out[k++] = pa[0]; out[k++] = pa[1]; out[k++] = pa[2]; out[k++] = pb[0]; out[k++] = pb[1]; out[k++] = pb[2];
    }
    return out;
  }
  const out = new Float32Array(t.edges.length * 4);
  let k = 0;
  for (const [a, b] of t.edges) {
    const pa = tilePoint(t, id, t.base[a]), pb = tilePoint(t, id, t.base[b]);
    out[k++] = pa[0]; out[k++] = pa[1]; out[k++] = pb[0]; out[k++] = pb[1];
  }
  return out;
}

/**
 * Tile under a point of the curve. `pointIndex` is the (fractional) index of
 * the drawing tip along the curve; prisms are in traversal order (2 points
 * each), the others are located geometrically. Returns -1 when outside.
 */
export function locateTile(t: TileSet, p: [number, number, number], pointIndex: number, prev = -1): number {
  if (t.kind === 'prisms') return Math.max(0, Math.min(t.n - 1, Math.floor(pointIndex / 2)));
  if (t.kind === 'grid') {
    const i = Math.floor(p[0] - t.origin[0]), j = Math.floor(p[1] - t.origin[1]);
    if (i < 0 || j < 0 || i >= t.side || j >= t.side) return -1;
    return j * t.side + i;
  }
  if (prev >= 0 && prev < t.n && tileContains(t, prev, p)) return prev;
  const g = t.index;
  const x = Math.floor((p[0] - g.x) / g.cell), y = Math.floor((p[1] - g.y) / g.cell);
  for (let j = Math.max(0, y - 1); j <= Math.min(g.rows - 1, y + 1); j++) {
    for (let i = Math.max(0, x - 1); i <= Math.min(g.cols - 1, x + 1); i++) {
      const k = j * g.cols + i;
      for (let n = g.offsets[k]; n < g.offsets[k + 1]; n++) { const id = g.ids[n]; if (tileContains(t, id, p)) return id; }
    }
  }
  return -1;
}

function prismPoint(aff: Float64Array, i: number, v: number[]): [number, number, number] {
  const j = i * 12;
  return [
    aff[j] * v[0] + aff[j + 1] * v[1] + aff[j + 2] * v[2] + aff[j + 3],
    aff[j + 4] * v[0] + aff[j + 5] * v[1] + aff[j + 6] * v[2] + aff[j + 7],
    aff[j + 8] * v[0] + aff[j + 9] * v[1] + aff[j + 10] * v[2] + aff[j + 11],
  ];
}

/** Point-in-polygon in the tile's own frame (inverse affine, then even-odd with an edge tolerance). */
export function tileContains(t: TilePolygons, id: number, p: [number, number, number]): boolean {
  const a = t.affines, j = id * 6;
  const x = p[0] - a[j + 2], y = p[1] - a[j + 5];
  const det = a[j] * a[j + 4] - a[j + 1] * a[j + 3];
  if (Math.abs(det) < 1e-18) return false;
  const u = (a[j + 4] * x - a[j + 1] * y) / det, v = (-a[j + 3] * x + a[j] * y) / det;
  let inside = false;
  const b = t.base, eps = 1e-7;
  for (let i = 0, k = b.length - 1; i < b.length; k = i++) {
    const [x1, y1] = b[k], [x2, y2] = b[i], dx = x2 - x1, dy = y2 - y1;
    if (Math.abs(dx * (v - y1) - dy * (u - x1)) <= eps * Math.hypot(dx, dy)
      && u >= Math.min(x1, x2) - eps && u <= Math.max(x1, x2) + eps && v >= Math.min(y1, y2) - eps && v <= Math.max(y1, y2) + eps) return true;
    if ((y1 > v) !== (y2 > v) && u < (dx * (v - y1)) / (y2 - y1) + x1) inside = !inside;
  }
  return inside;
}

/** Uniform grid over tile centres; cell = largest tile extent, so a point's tile is in its cell or a neighbour. */
export function buildIndex(t: TilePolygons): SpatialIndex {
  const centers = new Float64Array(t.n * 2);
  let cell = 0, x = Infinity, y = Infinity, xmax = -Infinity, ymax = -Infinity;
  for (let i = 0; i < t.n; i++) {
    let loX = Infinity, loY = Infinity, hiX = -Infinity, hiY = -Infinity;
    for (const v of t.base) {
      const [u, w] = tilePoint(t, i, v);
      loX = Math.min(loX, u); loY = Math.min(loY, w); hiX = Math.max(hiX, u); hiY = Math.max(hiY, w);
    }
    centers[2 * i] = (loX + hiX) / 2; centers[2 * i + 1] = (loY + hiY) / 2;
    cell = Math.max(cell, hiX - loX, hiY - loY);
    x = Math.min(x, loX); y = Math.min(y, loY); xmax = Math.max(xmax, hiX); ymax = Math.max(ymax, hiY);
  }
  cell = Math.max(cell, 1e-9);
  const cols = Math.floor((xmax - x) / cell) + 1, rows = Math.floor((ymax - y) / cell) + 1;
  const offsets = new Uint32Array(cols * rows + 1), ids = new Uint32Array(t.n), keys = new Uint32Array(t.n);
  for (let i = 0; i < t.n; i++) {
    const k = Math.floor((centers[2 * i + 1] - y) / cell) * cols + Math.floor((centers[2 * i] - x) / cell);
    keys[i] = k; offsets[k + 1]++;
  }
  for (let i = 1; i < offsets.length; i++) offsets[i] += offsets[i - 1];
  const cursor = offsets.slice();
  for (let i = 0; i < t.n; i++) ids[cursor[keys[i]]++] = i;
  return { x, y, cell, cols, rows, offsets, ids };
}
